import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/shadcn/card'
import { cn } from '@/lib/utils'
import { Type } from 'lucide-react'
import { trpc, type RouterOutput } from '@/config/trpc'
import { type PlatformType } from '@backend/lib/db/schema/platform'
import { HintLabel, InfoTooltip } from './info-tooltip'
import { PlatformGroupHeader } from './platform-group-header'

type CaptionRow = RouterOutput['analytics']['captionLength']['byBucket'][number]

const BUCKET_ORDER: Record<string, number> = {
  short: 0,
  medium: 1,
  long: 2,
  very_long: 3,
}

export function CaptionSweetSpotCard({ platforms }: { platforms: PlatformType[] }) {
  const { data, isLoading } = trpc.analytics.captionLength.useQuery({
    platforms: platforms.length > 0 ? platforms : undefined,
  })

  const grouped = data?.byBucket ? groupByPlatform(data.byBucket) : new Map()
  const isMultiPlatform = grouped.size > 1

  return (
    <Card className="relative overflow-hidden">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-sm font-medium">
          <Type size={14} className="text-muted-foreground" />
          Caption Length
          <InfoTooltip>
            Average engagement bucketed by caption length. Each platform is compared against its own
            baseline — caption norms differ between LinkedIn essays and Instagram hooks.
          </InfoTooltip>
        </CardTitle>
      </CardHeader>
      <CardContent>
        {isLoading && <SkeletonRows />}

        {data && !data.hasEnoughData && (
          <p className="text-muted-foreground py-4 text-xs">
            Need at least{' '}
            <span className="font-medium">
              {data.minBucketsPerPlatform} caption-length buckets on a platform
            </span>{' '}
            to compare. Vary your caption lengths to unlock this view.
          </p>
        )}

        {data && data.hasEnoughData && (
          <div className={cn(isMultiPlatform ? 'space-y-4' : 'space-y-3')}>
            {[...grouped.entries()].map(([platform, rows]) => {
              const ordered = [...rows].sort(
                (a, b) => (BUCKET_ORDER[a.id] ?? 99) - (BUCKET_ORDER[b.id] ?? 99),
              )
              return (
                <div key={platform}>
                  {isMultiPlatform && <PlatformGroupHeader platform={platform} />}
                  <CaptionBars rows={ordered} />
                </div>
              )
            })}
          </div>
        )}
      </CardContent>
    </Card>
  )
}

function CaptionBars({ rows }: { rows: CaptionRow[] }) {
  const max = Math.max(...rows.map((r) => r.avgEngagement))
  return (
    <div className="space-y-3">
      {rows.map((b) => {
        const widthPct = max > 0 ? (b.avgEngagement / max) * 100 : 0
        return (
          <div key={`${b.platform}-${b.id}`}>
            <div className="mb-1 flex items-center justify-between text-xs">
              <HintLabel hint={b.hint} className="text-foreground font-medium">
                {b.label}
              </HintLabel>
              <span
                className={cn(
                  'font-medium',
                  b.isBaseline
                    ? 'text-muted-foreground'
                    : b.relativeBoost && b.relativeBoost > 0
                      ? 'text-emerald-600 dark:text-emerald-500'
                      : 'text-muted-foreground',
                )}>
                {b.isBaseline ? (
                  <HintLabel hint="Lowest-performing length bucket on this platform. Others are shown relative to this one.">
                    baseline
                  </HintLabel>
                ) : b.relativeBoost != null ? (
                  `+${(b.relativeBoost * 100).toFixed(0)}%`
                ) : (
                  '—'
                )}
              </span>
            </div>
            <div className="bg-muted relative h-2 overflow-hidden rounded-full">
              <div
                className="bg-primary h-full rounded-full transition-all"
                style={{ width: `${widthPct}%` }}
              />
            </div>
          </div>
        )
      })}
    </div>
  )
}

function SkeletonRows() {
  return (
    <div className="space-y-3">
      {[0, 1, 2, 3].map((i) => (
        <div key={i}>
          <div className="bg-muted mb-1 h-3 w-20 animate-pulse rounded" />
          <div className="bg-muted h-2 animate-pulse rounded-full" />
        </div>
      ))}
    </div>
  )
}

function groupByPlatform<T extends { platform: PlatformType }>(rows: T[]): Map<PlatformType, T[]> {
  const m = new Map<PlatformType, T[]>()
  for (const row of rows) {
    const list = m.get(row.platform) ?? []
    list.push(row)
    m.set(row.platform, list)
  }
  return m
}
